import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { useI18n, LanguageSwitcher } from "@/i18n";
import { FooterSection } from "./home/FooterSection";

const sections = [
  { id: "overview", labelKey: "whatIsTitle" },
  { id: "installation", labelKey: "installTitle" },
  { id: "configuration", labelKey: "configTitle" },
  { id: "usage", labelKey: "howItWorksTitle" },
  { id: "example", labelKey: "exampleTitle" },
] as const;

export function SkillsPage() {
  const { t } = useI18n();

  const sidebarLabels: Record<string, string> = {
    whatIsTitle: t.skills.whatIsTitle,
    installTitle: t.skills.installTitle,
    configTitle: t.skills.configTitle,
    howItWorksTitle: t.skills.howItWorksTitle,
    exampleTitle: t.skills.exampleTitle,
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Language Switcher */}
      <div className="page-language-switcher">
        <LanguageSwitcher />
      </div>

      {/* Hero — minimal title bar */}
      <header className="w-full border-b border-[#e5e5e5] bg-white">
        <div className="mx-auto max-w-[1152px] px-6 py-10 md:py-14">
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight text-[#0a1733] mb-2">
            {t.skills.heroTitle}
          </h1>
          <p className="text-base text-[#7a818e] leading-relaxed">
            {t.skills.heroSubtitle}
          </p>
        </div>
      </header>

      {/* Two-column docs layout */}
      <div className="mx-auto max-w-[1152px] px-6">
        <div className="flex gap-12 lg:gap-16">
          {/* Sidebar — hidden on mobile */}
          <nav className="hidden md:block w-48 shrink-0 py-10">
            <div className="sticky top-8">
              <ul className="space-y-3">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-sm text-[#7a818e] hover:text-[#0a1733] transition-colors"
                    >
                      {sidebarLabels[s.labelKey]}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1 min-w-0 py-10 md:py-12">
            {/* Overview */}
            <section id="overview" className="pb-10 md:pb-12 border-b border-[#e5e5e5]">
              <h2 className="text-xl md:text-2xl font-medium text-[#0a1733] mb-4">
                {t.skills.whatIsTitle}
              </h2>
              <p className="text-[#7a818e] leading-relaxed">
                {t.skills.whatIsDesc}
              </p>
            </section>

            {/* Installation */}
            <section id="installation" className="py-10 md:py-12 border-b border-[#e5e5e5]">
              <h2 className="text-xl md:text-2xl font-medium text-[#0a1733] mb-4">
                {t.skills.installTitle}
              </h2>
              <div className="bg-white border border-[#e5e5e5] rounded-[10px] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[#e5e5e5]">
                  <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
                  <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
                  <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
                  <span className="ml-2 text-xs text-[#7a818e] font-mono">terminal</span>
                </div>
                <div className="p-5 font-mono text-sm">
                  <span className="text-[#ff4a01]">$</span>{" "}
                  <span className="text-[#1a1a1a]">{t.skills.installCommand}</span>
                </div>
              </div>
            </section>

            {/* Configuration */}
            <section id="configuration" className="py-10 md:py-12 border-b border-[#e5e5e5]">
              <h2 className="text-xl md:text-2xl font-medium text-[#0a1733] mb-3">
                {t.skills.configTitle}
              </h2>
              <p className="text-[#7a818e] mb-5">{t.skills.configDesc}</p>
              <div className="bg-white border border-[#e5e5e5] rounded-[10px] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[#e5e5e5]">
                  <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
                  <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
                  <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
                  <span className="ml-2 text-xs text-[#7a818e] font-mono">.env.govilo</span>
                </div>
                <div className="p-5 font-mono text-sm space-y-1">
                  <div className="text-[#7a818e]"># .env.govilo</div>
                  <div>
                    <span className="text-[#0a1733]">GOVILO_API_KEY</span>
                    <span className="text-[#7a818e]">=</span>
                    <span className="text-[#ff4a01]">your-api-key-here</span>
                  </div>
                  <div>
                    <span className="text-[#0a1733]">GOVILO_PAYOUT_ADDRESS</span>
                    <span className="text-[#7a818e]">=</span>
                    <span className="text-[#ff4a01]">0xYourWalletAddress</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Usage — vertical numbered steps */}
            <section id="usage" className="py-10 md:py-12 border-b border-[#e5e5e5]">
              <h2 className="text-xl md:text-2xl font-medium text-[#0a1733] mb-6">
                {t.skills.howItWorksTitle}
              </h2>
              <ol className="space-y-6">
                {[
                  { title: t.skills.step1Title, desc: t.skills.step1Desc },
                  { title: t.skills.step2Title, desc: t.skills.step2Desc },
                  { title: t.skills.step3Title, desc: t.skills.step3Desc },
                ].map((step, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#f5f5f5] text-xs font-semibold text-[#7a818e] shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="text-base font-medium text-[#0a1733] mb-1">
                        {step.title}
                      </h3>
                      <p className="text-sm text-[#7a818e] leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Example */}
            <section id="example" className="py-10 md:py-12 border-b border-[#e5e5e5]">
              <h2 className="text-xl md:text-2xl font-medium text-[#0a1733] mb-4">
                {t.skills.exampleTitle}
              </h2>
              <blockquote className="border-l-2 border-[#e5e5e5] pl-5 py-1">
                <p className="text-[#0a1733] italic leading-relaxed">
                  {t.skills.examplePrompt}
                </p>
              </blockquote>
            </section>

            {/* CTA */}
            <section className="py-10 md:py-12">
              <h2 className="text-xl md:text-2xl font-medium text-[#0a1733] mb-2">
                {t.skills.ctaTitle}
              </h2>
              <p className="text-[#7a818e] mb-6">{t.skills.ctaDesc}</p>
              <Link
                href="/dashboard/api-keys"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#ff4a01] text-white text-sm font-medium rounded-[10px] hover:bg-[#e54400] transition-colors"
              >
                {t.skills.ctaButton}
                <ArrowRight className="size-4" />
              </Link>
            </section>
          </main>
        </div>
      </div>

      <FooterSection />
    </div>
  );
}
